import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ContactUnitsFilterParams,
  ContactUnitsListResponse,
  ContactUnitType,
  CreateContactUnitParams,
  UpdateContactUnitParams
} from '@/types/contact-unit'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  bulkDeleteContactUnits,
  createContactUnit,
  deleteContactUnit,
  fetchContactUnit,
  fetchContactUnits,
  sortContactUnit,
  updateContactUnit
} from '@/lib/api'

type ContactUnitStateType = {
  data: ContactUnitType | null
  isLoading: boolean
  error: string | null
}

type ContactUnitsListStateType = {
  data: ContactUnitsListResponse | null
  isLoading: boolean
  error: string | null
}

type ContactUnitsStoreState = {
  contactUnit: ContactUnitStateType
  contactUnitsList: ContactUnitsListStateType
  filters: ContactUnitsFilterParams
  selectedContactUnitIds: string[]
  setFilters: (filters: Partial<ContactUnitsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchContactUnits: (params?: ContactUnitsFilterParams) => Promise<ContactUnitsListResponse>
  resetContactUnitsList: () => void

  // Sort contact unit
  sortContactUnit: (id: string, type: SortOrderEnum) => Promise<ContactUnitType>

  // Contact Unit operations
  getContactUnit: (id: string) => Promise<ContactUnitType>
  createContactUnit: (params: CreateContactUnitParams) => Promise<ContactUnitType>
  updateContactUnit: (id: string, params: UpdateContactUnitParams) => Promise<ContactUnitType>
  deleteContactUnit: (id: string) => Promise<void>
  bulkDeleteContactUnits: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectContactUnit: (id: string) => void
  deselectContactUnit: (id: string) => void
  toggleContactUnitSelection: (id: string) => void
  selectAllContactUnits: (contactUnits: ContactUnitType[]) => void
  deselectAllContactUnits: () => void
  isContactUnitSelected: (id: string) => boolean
  resetContactUnit: () => void
}

const defaultFilters: ContactUnitsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialContactUnitState: ContactUnitStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialContactUnitsListState: ContactUnitsListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useContactUnitsStore = create<ContactUnitsStoreState>()(
  devtools((set, get) => ({
    contactUnit: initialContactUnitState,
    contactUnitsList: initialContactUnitsListState,
    selectedContactUnitIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof ContactUnitsFilterParams]
          } else {
            updatedFilters[key as keyof ContactUnitsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: ContactUnitsFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ContactUnitsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ContactUnitsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getContactUnit: async id => {
      set(state => ({
        contactUnit: {
          ...state.contactUnit,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchContactUnit(id)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createContactUnit: async params => {
      set(state => ({
        contactUnit: {
          ...state.contactUnit,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createContactUnit(params)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateContactUnit: async (id, params) => {
      set(state => ({
        contactUnit: {
          ...state.contactUnit,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateContactUnit(id, params)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          contactUnit: {
            ...state.contactUnit,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteContactUnit: async id => {
      try {
        await deleteContactUnit(id)
      } catch (error) {
        throw error
      }
    },

    bulkDeleteContactUnits: async ids => {
      try {
        return await bulkDeleteContactUnits({ ids })
      } catch (error) {
        throw error
      }
    },

    fetchContactUnits: async params => {
      set(state => ({
        contactUnitsList: {
          ...state.contactUnitsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchContactUnits(params || get().filters)

        set(state => ({
          contactUnitsList: {
            ...state.contactUnitsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          contactUnitsList: {
            ...state.contactUnitsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetContactUnitsList: () => {
      set({ contactUnitsList: initialContactUnitsListState })
    },

    sortContactUnit: async (id, type) => {
      try {
        return await sortContactUnit(id, type)
      } catch (error) {
        throw error
      }
    },

    selectContactUnit: id => {
      set(state => {
        if (state.selectedContactUnitIds.includes(id)) {
          return state
        }
        return {
          selectedContactUnitIds: [...state.selectedContactUnitIds, id]
        }
      })
    },

    deselectContactUnit: id => {
      set(state => ({
        selectedContactUnitIds: state.selectedContactUnitIds.filter(contactUnitId => contactUnitId !== id)
      }))
    },

    toggleContactUnitSelection: id => {
      set(state => {
        const isSelected = state.selectedContactUnitIds.includes(id)
        if (isSelected) {
          return {
            selectedContactUnitIds: state.selectedContactUnitIds.filter(contactUnitId => contactUnitId !== id)
          }
        } else {
          return {
            selectedContactUnitIds: [...state.selectedContactUnitIds, id]
          }
        }
      })
    },

    selectAllContactUnits: contactUnits => {
      set({
        selectedContactUnitIds: contactUnits.map(contactUnit => contactUnit.id)
      })
    },

    deselectAllContactUnits: () => {
      set({ selectedContactUnitIds: [] })
    },

    isContactUnitSelected: id => {
      return get().selectedContactUnitIds.includes(id)
    },

    resetContactUnit: () => {
      set({ contactUnit: initialContactUnitState })
    }
  }))
)

export default useContactUnitsStore
